import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  Formulation,
  FormulationWrite,
  FormulationsResponse,
  ImportTemplateResponse,
  IngredientSearchResponse,
  TransitionWrite,
} from './types'

const ROOT = '/api/formulations'

export interface UseFormulationsOptions {
  q?: string
  /** Sprint 08b: include soft-deleted rows. */
  includeArchived?: boolean
}

export function useFormulations(opts: UseFormulationsOptions | string = {}) {
  // Backwards-compat: an earlier call site passed a bare query string.
  const normalized: UseFormulationsOptions =
    typeof opts === 'string' ? { q: opts } : opts
  const q = (normalized.q ?? '').trim()
  const includeArchived = Boolean(normalized.includeArchived)

  return useQuery({
    queryKey: ['formulations', 'list', q, includeArchived],
    queryFn: () => {
      const search = new URLSearchParams()
      if (q) search.set('q', q)
      if (includeArchived) search.set('include_archived', '1')
      const qs = search.toString()
      return jsonFetch<FormulationsResponse>(qs ? `${ROOT}?${qs}` : ROOT)
    },
    staleTime: 15_000,
  })
}

export function useFormulation(id: number | null) {
  return useQuery({
    queryKey: ['formulations', 'detail', id],
    queryFn: () => jsonFetch<Formulation>(`${ROOT}/${id}`),
    enabled: id !== null,
    staleTime: 15_000,
  })
}

export function useCreateFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: FormulationWrite) =>
      jsonFetch<Formulation>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
    },
  })
}

export interface UpdateFormulationVars {
  id: number
  body: FormulationWrite
}

export function useUpdateFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UpdateFormulationVars) =>
      jsonFetch<Formulation>(`${ROOT}/${vars.id}`, {
        method: 'PUT',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
      void qc.invalidateQueries({ queryKey: ['formulations', 'detail', vars.id] })
    },
  })
}

export function useDeleteFormulation() {
  const qc = useQueryClient()
  return useMutation({
    // Sprint 08b: this is now a soft-delete on the backend (sets
    // deleted_at). We keep the same mutation hook + name so existing
    // callers don't re-wire; only the response shape has shifted to
    // include `archived_at`.
    mutationFn: (id: number) =>
      jsonFetch<{ ok: true; deleted_id: number; archived_at: string }>(
        `${ROOT}/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
    },
  })
}

export function useRestoreFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<Formulation>(`${ROOT}/${id}/restore`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
      void qc.invalidateQueries({ queryKey: ['formulations', 'detail', id] })
    },
  })
}

export interface TransitionVars {
  id: number
  body: TransitionWrite
}

export function useTransitionFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: TransitionVars) =>
      jsonFetch<Formulation>(`${ROOT}/${vars.id}/transition`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
      void qc.invalidateQueries({ queryKey: ['formulations', 'detail', vars.id] })
    },
  })
}

/**
 * Search Odoo product.template for ingredient picker — backs the
 * IngredientPicker component. Returns `{id, name, default_code,
 * inci_names}` per match.
 */
export function useIngredientSearch(q: string) {
  const trimmed = q.trim()
  return useQuery({
    queryKey: ['formulations', 'ingredient-search', trimmed],
    queryFn: () =>
      jsonFetch<IngredientSearchResponse>(
        `${ROOT}/search/ingredients?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  })
}

/**
 * POST /api/formulations/import-template — multipart upload.
 *
 * jsonFetch assumes JSON, so we use fetch() directly for the multipart
 * path. Auth cookie is sent automatically; we only need to keep this
 * same-origin relative URL so middleware proxying works.
 */
export function useImportTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File): Promise<ImportTemplateResponse> => {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${ROOT}/import-template`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Import failed: ${res.status}`)
      }
      return (await res.json()) as ImportTemplateResponse
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
    },
  })
}
